import React, { useCallback, useEffect, useState } from 'react';

import { getDocument } from '@atlaskit/browser-apis';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { Popup } from '@atlaskit/editor-common/ui';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import type { AgentManagedExtensionPlugin } from '../../common/agentManagedExtensionPluginType';
import { findAgentManagedExtension } from '../../common/utils';

import { ConfigurePanel } from './ConfigurePanel';

interface AgentManagedExtensionConfigurePopupProps {
	api: ExtractInjectionAPI<AgentManagedExtensionPlugin> | undefined;
	editorView: EditorView;
	mountPoint?: HTMLElement;
}

const POPUP_STYLE = { width: 500 } as const;
const POPUP_OFFSET: [number, number] = [0, 8];

const TOOLBAR_SELECTOR = '[data-testid="editor-floating-toolbar"]';

/**
 * Renders the inline configuration popup for the agent-managed extension
 * currently in (or around) the selection — when the plugin has been told to open the popup.
 */
export const AgentManagedExtensionConfigurePopup = ({
	api,
	editorView,
	mountPoint,
}: AgentManagedExtensionConfigurePopupProps): React.JSX.Element | null => {
	const agentManagedExtensionState = useSharedPluginStateWithSelector(
		api,
		['agentManagedExtension'],
		({ agentManagedExtensionState }) => agentManagedExtensionState,
	);

	const handleSave = useCallback(
		(_prompt: string) => {
			// TODO: CCINT-7833 - persist the prompt to the agent-managed-object backend
			// 	via a new `updateAgentManagedObject` command.
			api?.agentManagedExtension?.actions?.closeConfig();
		},
		[api],
	);

	const handleCancel = useCallback(() => {
		// Don't persist any changes; only close
		api?.agentManagedExtension?.actions?.closeConfig();
	}, [api]);

	const isOpen = agentManagedExtensionState?.isConfigOpen ?? false;

	// Derive the target block from the current selection
	const found = isOpen ? findAgentManagedExtension(editorView.state) : null;
	const dom = found ? editorView.nodeDOM(found.pos) : null;
	const blockElement = dom instanceof HTMLElement ? dom : null;

	// Prefer anchoring the popup to the floating toolbar so it appears
	// just below or above the toolbar. The toolbar is rendered in a portal
	// outside the block DOM, so we look it up after mount via useEffect.
	// On a fresh insert the toolbar may not yet exist on the first render,
	// so we retry on the next animation frame.
	const [toolbarElement, setToolbarElement] = useState<HTMLElement | null>(null);

	useEffect(() => {
		if (!isOpen) {
			if (toolbarElement) {
				setToolbarElement(null);
			}
			return;
		}

		// Already resolved — nothing to do
		if (toolbarElement) {
			return;
		}

		// Try immediately — toolbar may already be in the DOM
		const toolbar = getDocument()?.querySelector<HTMLElement>(TOOLBAR_SELECTOR);
		if (toolbar) {
			setToolbarElement(toolbar);
			return;
		}

		// Toolbar not yet in DOM — retry on the next animation frame
		const id = requestAnimationFrame(() => {
			const el = getDocument()?.querySelector<HTMLElement>(TOOLBAR_SELECTOR);
			if (el) {
				setToolbarElement(el);
			}
		});
		return () => cancelAnimationFrame(id);
	}, [isOpen, toolbarElement]);

	const targetElement = toolbarElement ?? blockElement;

	if (!isOpen || !found || !targetElement) {
		return null;
	}

	return (
		<Popup
			alignX="center"
			alignY="bottom"
			target={targetElement}
			mountTo={mountPoint}
			zIndex={100}
			fitWidth={500}
			fitHeight={250}
			preventOverflow={true}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- `Popup` accepts only an inline style prop for its outer wrapper
			style={POPUP_STYLE}
			offset={POPUP_OFFSET}
			ariaLabel="Agent-managed block configuration"
		>
			<ConfigurePanel
				/* TODO: CCINT-7833 - once the agent-managed-object hydration path is in place,
				 * 	derive `initialPrompt` from the hydrated `aiManagedObject` for the block
				 * 	at `found.node.attrs.localId`. For now the prompt is intentionally not
				 * 	pre-populated because nothing yet hydrates it. */
				initialPrompt=""
				onSave={handleSave}
				onCancel={handleCancel}
			/>
		</Popup>
	);
};
